// src/pages/Swap/utils/SwapUtils.js - Enhanced for better contract compatibility
import { ethers, Contract, formatUnits, parseUnits } from 'ethers';
import { ETH_ADDRESS, BITDOG_SWAP_ABI, ERC20_ABI } from './SwapConstants';

/**
 * Helper function to check if a token is native (ETH/BROCK)
 * @param {string} address - Token address
 * @returns {boolean} - True if token is native
 */
export const isNativeToken = (address) => {
  if (!address) return false;
  return address.toLowerCase() === ETH_ADDRESS.toLowerCase();
};

/**
 * Format amounts for display with appropriate decimal formatting
 * @param {string|number} amount - Amount to format
 * @param {number} decimals - Number of decimals to display (default: 6)
 * @returns {string} - Formatted amount
 */
export const formatAmountForDisplay = (amount, decimals = 6) => {
  if (!amount) return '0';
  
  // Convert to number and format with specified decimals
  const num = parseFloat(amount);
  
  // For very small numbers
  if (num < 0.000001 && num > 0) {
    return num.toExponential(4);
  }
  
  // For regular numbers
  return num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: decimals
  });
};

/**
 * Format balance for display (e.g. in UI)
 * @param {string} balance - Raw balance
 * @returns {string} - Formatted balance
 */
export const formatBalanceForDisplay = (balance) => {
  if (!balance) return '0.0000';
  
  const num = parseFloat(balance);
  
  // For very small numbers (show more decimals)
  if (num > 0 && num < 0.0001) {
    return num.toExponential(4); // Or use more decimal places: num.toFixed(8)
  }
  
  // For normal values
  return num.toFixed(4);
};

/**
 * Get BitDogSwap aggregator contract instance
 * @param {object} provider - Ethers provider
 * @param {string} aggregatorAddress - BitDogSwap aggregator contract address
 * @param {object} currentWallet - User's wallet (if signing is needed)
 * @param {boolean} withSigner - Whether to include a signer or not
 * @returns {Contract|null} - BitDogSwap contract instance
 */
export const getBitDogSwapContract = (provider, aggregatorAddress, currentWallet, withSigner = false) => {
  if (!provider) {
    console.error("Missing provider in getBitDogSwapContract");
    return null;
  }
  
  if (!aggregatorAddress) {
    console.error("Missing aggregator address in getBitDogSwapContract");
    return null;
  }
  
  // Don't attempt to create a contract for API-based aggregators
  if (aggregatorAddress === 'SushiSwap' || aggregatorAddress === 'KyberSwap') {
    console.error(`Cannot create contract for ${aggregatorAddress} - use API instead`);
    return null;
  }
  
  // Validate address format
  if (!aggregatorAddress.startsWith('0x') || aggregatorAddress.length !== 42) {
    console.error(`Invalid aggregator address format: ${aggregatorAddress}`);
    return null;
  }
  
  // SIMPLIFIED: Use minimal ABI for better compatibility with different aggregator implementations
  const BITDOG_SWAP_BASIC_ABI = [
    // Core swap and quote functions - these must be supported
    "function getEthToTokenQuote(address tokenOut, uint256 amountIn) external view returns (address bestRouter, uint256 estimatedTokens)",
    "function getTokenToEthQuote(address tokenIn, uint256 amountIn) external view returns (address bestRouter, uint256 estimatedEth)",
    "function getTokenToTokenQuote(address tokenIn, address tokenOut, uint256 amountIn) external view returns (address sourceRouter, address destRouter, uint256 estimatedTokens)",
    "function swap(address tokenIn, address tokenOut, uint256 amountIn, uint256 amountOutMin, uint256 deadline) external payable returns (uint256 amountReceived)",
    
    // Optional fee methods - these may not be supported by all contracts
    "function getFeePercentage() external view returns (uint256)",
    "function getFeeCollectionMethod() external view returns (bool)"
  ];
  
  try {
    console.log(`Creating contract instance for aggregator at ${aggregatorAddress}`);
    
    if (withSigner) {
      if (!currentWallet?.privateKey) {
        console.error("No wallet provided for signed contract");
        return null;
      }
      const wallet = new ethers.Wallet(currentWallet.privateKey, provider);
      return new ethers.Contract(aggregatorAddress, BITDOG_SWAP_BASIC_ABI, wallet);
    } else {
      return new ethers.Contract(aggregatorAddress, BITDOG_SWAP_BASIC_ABI, provider);
    }
  } catch (err) {
    console.error("Error creating BitDogSwap contract:", err);
    return null;
  }
};

/**
 * Fetch a user's token balance
 * @param {object} provider - Ethers provider
 * @param {string} address - User address
 * @param {object} tokenObj - Token object
 * @returns {Promise<string>} - Token balance as string
 */
export const fetchTokenBalance = async (provider, userAddress, tokenObj) => {
  if (!provider || !userAddress || !tokenObj) {
    console.log("Missing required parameters for fetchTokenBalance");
    return '0.0';
  }
  
  try {
    console.log("Fetching balance for token:", tokenObj.symbol);
    
    // Get the token's actual decimals (not defaulting to 18)
    const tokenDecimals = await getActualTokenDecimals(provider, tokenObj);
    console.log("Token decimals:", tokenDecimals);
    
    if (isNativeToken(tokenObj.address)) {
      // Native coin
      console.log("Fetching native token balance");
      const balBN = await provider.getBalance(userAddress);
      const formattedBal = formatUnits(balBN, tokenDecimals);
      console.log("Native balance:", formattedBal);
      return formattedBal;
    } else {
      // ERC-20
      console.log("Fetching ERC-20 token balance");
      const contract = new Contract(tokenObj.address, ["function balanceOf(address) view returns (uint256)"], provider);
      const balBN = await contract.balanceOf(userAddress);
      const formattedBal = formatUnits(balBN, tokenDecimals);
      console.log("ERC-20 balance:", formattedBal);
      return formattedBal;
    }
  } catch (err) {
    console.error(`Error fetching balance for ${tokenObj.symbol}:`, err);
    return '0.0';
  }
};

/**
 * Helper to get actual token decimals directly from the contract
 * @param {object} provider - Ethers provider
 * @param {object} tokenObj - Token object
 * @returns {Promise<number>} - Token decimals as a number
 */
export const getActualTokenDecimals = async (provider, tokenObj) => {
  // Return standard 18 for native tokens
  if (isNativeToken(tokenObj.address)) return 18;
  
  // If we already have numeric decimals, return them
  if (typeof tokenObj.decimals === 'number') return tokenObj.decimals;
  
  // If we have string decimals, parse them to a number
  if (typeof tokenObj.decimals === 'string') {
    const parsed = parseInt(tokenObj.decimals, 10);
    if (!isNaN(parsed)) return parsed;
  }
  
  // For tokens without decimals, try to fetch from contract
  try {
    const contract = new Contract(
      tokenObj.address, 
      ["function decimals() view returns (uint8)"], 
      provider
    );
    const decimals = await contract.decimals();
    return typeof decimals === 'string' ? parseInt(decimals, 10) : Number(decimals);
  } catch (error) {
    console.warn(`Could not get decimals for ${tokenObj.symbol}, using 18 as fallback`, error);
    return 18;
  }
};

/**
 * Check if a token has sufficient allowance for the aggregator
 * @param {object} provider - Ethers provider
 * @param {string} userAddress - User address
 * @param {object} inputToken - Input token object
 * @param {string} aggregatorAddress - Aggregator/Router address
 * @param {string} amountIn - Amount to check allowance for
 * @returns {Promise<boolean>} - True if allowance is sufficient
 */
export const checkAllowance = async (provider, userAddress, inputToken, aggregatorAddress, amountIn) => {
  // Verify all required parameters
  if (!provider) {
    console.error("Missing provider in checkAllowance");
    return false;
  }
  
  if (!userAddress) {
    console.error("Missing userAddress in checkAllowance");
    return false;
  }
  
  if (!inputToken?.address) {
    console.error("Missing input token in checkAllowance");
    return false;
  }
  
  // CRITICAL FIX: Check if aggregatorAddress is a special value and exit early
  if (aggregatorAddress === 'SushiSwap' || aggregatorAddress === 'KyberSwap') {
    console.error(`Invalid aggregator address: ${aggregatorAddress} string was passed instead of actual contract address`);
    return false;
  }
  
  if (!aggregatorAddress || typeof aggregatorAddress !== 'string' || !aggregatorAddress.startsWith('0x')) {
    console.error(`Invalid aggregatorAddress in checkAllowance: ${aggregatorAddress}`);
    return false;
  }
  
  // Native token doesn't need approval
  if (isNativeToken(inputToken.address)) {
    return true;
  }
  
  try {
    console.log(`Checking allowance for token ${inputToken.symbol} (${inputToken.address}) to spender ${aggregatorAddress}`);
    
    const tokenContract = new Contract(inputToken.address, ERC20_ABI, provider);
    
    // Get decimals - ensure it's a number
    const decimals = await getActualTokenDecimals(provider, inputToken);
    
    // If amountIn is not set, check for any approval
    if (!amountIn || parseFloat(amountIn) <= 0) {
      const allowance = await tokenContract.allowance(userAddress, aggregatorAddress);
      return allowance > 0;
    }
    
    // Otherwise check if approval is sufficient for the transaction
    const allowance = await tokenContract.allowance(userAddress, aggregatorAddress);
    const amountInWei = parseUnits(amountIn.toString(), decimals);
    
    console.log("Checking allowance:", {
      token: inputToken.symbol,
      decimals: decimals,
      allowance: allowance.toString(),
      required: amountInWei.toString(),
      spender: aggregatorAddress,
      sufficient: allowance >= amountInWei
    });
    
    return allowance >= amountInWei;
  } catch (err) {
    console.error("Error checking allowance:", err);
    return false;
  }
};

/**
 * Get price impact class for styling
 * @param {number|null} priceImpact - Price impact percentage
 * @returns {string} - CSS class
 */
export const getPriceImpactClass = (priceImpact) => {
  if (!priceImpact && priceImpact !== 0) return '';
  
  if (priceImpact < 1) return 'impact-low';
  if (priceImpact < 3) return 'impact-medium';
  return 'impact-high';
};